import { ThemeProvider } from '@mui/material';
import * as Sentry from '@sentry/react';
import ReactDOM from 'react-dom/client';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import { ExistingAppointmentsPage } from './Components/Appointments/ExistingAppointments';
import BodyComposition from './Components/BodyComposition/BodyComposition';
import ErrorBoundary from './Components/ErrorBoundary/ErrorBoundary';
import GroupClassDetail from './Components/GroupClasses/GroupClassDetail';
import GroupClasses from './Components/GroupClasses/GroupClasses';
import LoginPage from './Components/Login/Login';
import MainMenuPage from './Components/MainMenu/MainMenuPage';
import Meeting from './Components/Meeting/Meeting';
import PrivateRoute from './Components/PrivateRoute/PrivateRoute';
import Scheduling from './Components/Scheduling/Scheduling';
import SchedulingLink from './Components/SchedulingLink/SchedulingLink';
import SelfSignIn from './Components/SelfSignIn/SelfSignIn';
import Toast, { ToastContainer } from './Components/Toast/Toast';
import { AuthProvider } from './contexts/auth/context';
import { FeaturesProvider } from './contexts/features/context';
import './index.css';
import { ENVIRONMENT, SENTRY_DSN } from './lib/environment';
import { isAxiosNetworkError } from './lib/request';
import MixpanelClient from './lib/tracking/mixpanel';
import reportWebVitals from './reportWebVitals';
import { RouteConstants } from './route-constants';
import theme from './theme';

const router = createBrowserRouter([
  {
    path: RouteConstants.Root,
    element: <MainMenuPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: RouteConstants.Login,
    element: <LoginPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: RouteConstants.ShortSelfSignIn,
    element: <SelfSignIn />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: RouteConstants.SelfSignIn,
    element: <SelfSignIn />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: RouteConstants.AppointmentsForMember,
    element: <ExistingAppointmentsPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: RouteConstants.AppointmentDetailsForMember,
    element: <ExistingAppointmentsPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: RouteConstants.AppointmentsForMe,
    element: <ExistingAppointmentsPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: RouteConstants.AppointmentDetailsForMe,
    element: <ExistingAppointmentsPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: RouteConstants.Scheduling,
    element: <Scheduling />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: RouteConstants.SchedulingLink,
    element: <SchedulingLink />,
  },
  {
    path: RouteConstants.GroupClasses,
    element: <GroupClasses />,
  },
  {
    path: RouteConstants.GroupClassesDetails,
    element: <GroupClassDetail />,
  },
  {
    path: RouteConstants.Meeting,
    element: <Meeting />,
  },
  {
    path: RouteConstants.Error,
    element: <ErrorBoundary />,
  },
  {
    element: <PrivateRoute />,
    children: [
      {
        path: RouteConstants.BodyComposition,
        element: <BodyComposition />,
      },
    ],
  },
]);

// Initialize Sentry
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    beforeSend(event, hint) {
      // Filter out only axios network errors (not HTTP errors like 4xx/5xx)
      if (isAxiosNetworkError(hint?.originalException)) {
        Toast.error('Unable to connect. Please check your internet connection and try again');
        return null;
      }

      // From this point, errors are reportable

      // Don't send errors if we're on development
      if (ENVIRONMENT === 'development') {
        console.error('Sentry captured error:', event);
        return null;
      }
      return event;
    },
    // Capture console errors as breadcrumbs
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === 'console' && breadcrumb.level === 'error') {
        return breadcrumb;
      }
      return breadcrumb;
    },
  });

  // Capture unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    Sentry.captureException(event.reason);
  });

  // Capture global errors
  window.addEventListener('error', (event) => {
    Sentry.captureException(event.error || new Error(event.message));
  });
}

MixpanelClient.init();

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <>
    <ThemeProvider theme={theme}>
      <Sentry.ErrorBoundary fallback={<ErrorBoundary />}>
        <AuthProvider>
          <FeaturesProvider>
            <RouterProvider router={router} />
          </FeaturesProvider>
        </AuthProvider>
      </Sentry.ErrorBoundary>
    </ThemeProvider>
    <ToastContainer />
  </>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
